import { Box, Skeleton, Stack, Typography } from "@mui/material";
import {
  FlexGrid,
  FlexGridCellTemplate,
  FlexGridColumn,
} from "@grapecity/wijmo.react.grid";
import { FlexGridFilter } from "@grapecity/wijmo.react.grid.filter";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getExpensesTotals } from "src/apis/controllers/expenses";
import { useLoading } from "src/hooks/useLoading";
import { CssTextField } from "qubCommon/Inputs";
import ExpenseGuideLinks from "./ExpenseGuideLinks";

const normalizeDate = (value) => (value ? new Date(value) : null);

const normalizeRow = (row) => ({
  ...row,
  applicationDate: normalizeDate(row?.applicationDate),
  consumptionPerc: row?.consumptionPerc ?? row?.ConsumptionPerc,
  returnedForCorrectionCount:
    row?.returnedForCorrectionCount ?? row?.ReturnedForCorrectionCount,
});

const MhdExpenseSummaryExplorer = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const [searchText, setSearchText] = useState("");
  const [expensesTotals, setExpensesTotals] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    const fetchExpensesTotals = async () => {
      setLoading(true);
      try {
        const res = await getExpensesTotals();

        if (res?.status === "error") {
          enqueueSnackbar(res?.message ?? "Προέκυψε σφάλμα κατά τη φόρτωση των δαπανών.", {
            variant: "error",
          });
          setExpensesTotals([]);
          return;
        }

        setExpensesTotals((res?.data ?? []).map(normalizeRow));
      } catch (error) {
        enqueueSnackbar(
          error?.message ?? "Προέκυψε σφάλμα κατά τη φόρτωση των δαπανών.",
          { variant: "error" }
        );
        setExpensesTotals([]);
      } finally {
        setHasLoaded(true);
        setLoading(false);
      }
    };

    fetchExpensesTotals();
  }, [setLoading]);

  const filteredExpensesTotals = useMemo(() => {
    const normalizedSearch = searchText.trim().toLocaleLowerCase("el-GR");

    if (!normalizedSearch) return expensesTotals;

    return expensesTotals.filter((row) =>
      [
        row.protocolNumber,
        row.name,
        row.status,
        row.applicationDateFormatted,
        row.instituteName,
        row.instituteAbbr,
        row.typeName,
        row.typeAbbr,
        row.budgetAmount,
        row.expensesCount,
        row.expensesAmount,
        row.approvedAmount,
        row.paidAmount,
        row.availableAmount,
        row.consumptionPerc,
        row.pendingReviewCount,
        row.returnedForCorrectionCount,
      ]
        .filter((value) => value !== undefined && value !== null)
        .some((value) =>
          String(value).toLocaleLowerCase("el-GR").includes(normalizedSearch)
        )
    );
  }, [expensesTotals, searchText]);

  const initializeGrid = useCallback(
    (grid) => {
      grid.hostElement.addEventListener("dblclick", (event) => {
        const hitTest = grid.hitTest(event);
        const selectedItem = grid.rows[hitTest.row]?.dataItem;

        const selectedApplicationId =
          selectedItem?.applicationId ?? selectedItem?.id;

        if (hitTest.panel === grid.cells && selectedApplicationId) {
          navigate(`/expenses/${selectedApplicationId}`);
        }
      });
    },
    [navigate]
  );

  const renderExpensesContent = () => {
    if (!hasLoaded) {
      return (
        <Stack>
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </Stack>
      );
    }

    if (filteredExpensesTotals.length === 0) {
      return (
        <Typography align="center" variant="h4" sx={{ py: 4 }}>
          Δεν βρέθηκαν δαπάνες.
        </Typography>
      );
    }

    return (
      <FlexGrid
        anchorCursor
        autoGenerateColumns={false}
        className="explorer-grid"
        headersVisibility="Column"
        initialized={initializeGrid}
        isReadOnly
        itemsSource={filteredExpensesTotals}
        selectionMode="Row"
        style={{ minHeight: 360 }}
      >
        <FlexGridFilter />
        <FlexGridColumn header="#" width={70} align="right">
          <FlexGridCellTemplate
            cellType="Cell"
            template={(ctx) => (ctx.row?.index ?? 0) + 1}
          />
        </FlexGridColumn>
        <FlexGridColumn
          binding="protocolNumber"
          header="Α/Α"
          width={120}
          align="right"
        >
          <FlexGridCellTemplate
            cellType="Cell"
            template={(ctx) =>
              ctx.item?.protocolNumber === undefined ||
              ctx.item?.protocolNumber === null
                ? ""
                : String(ctx.item.protocolNumber)
            }
          />
        </FlexGridColumn>
        <FlexGridColumn binding="name" header="ΜΗΔ" width={260} minWidth={220} />
        <FlexGridColumn
          binding="status"
          header="ΚΑΤ/ΣΗ"
          width={130}
          minWidth={110}
        />
        <FlexGridColumn
          binding="budgetAmount"
          header="Π/Υ"
          format={"c\u20ac"}
          width={170}
          align="right"
        />
        <FlexGridColumn
          binding="expensesCount"
          header="# ΔΑΠ."
          width={95}
          format="d"
          align="right"
        />
        <FlexGridColumn
          binding="expensesAmount"
          header="ΔΑΠΑΝΕΣ"
          format={"c\u20ac"}
          width={170}
          align="right"
        />
        <FlexGridColumn
          binding="approvedAmount"
          header="ΕΓΚΕΚΡΙΜΕΝΟ"
          width={170}
          align="right"
          format={"c\u20ac"}
        />
        <FlexGridColumn
          binding="paidAmount"
          header="ΠΛΗΡΩΜΕΣ"
          format={"c\u20ac"}
          width={170}
          align="right"
        />
        <FlexGridColumn
          binding="availableAmount"
          header="ΥΠΟΛΟΙΠΟ"
          width={170}
          align="right"
          format={"c\u20ac"}
        />
        <FlexGridColumn
          binding="consumptionPerc"
          header="% ΑΝΑΛ."
          width={105}
          align="right"
          format="p2"
        />
        <FlexGridColumn
          binding="pendingReviewCount"
          header="# ΕΛΕΓΧ."
          width={100}
          align="right"
          format="d"
        />
        <FlexGridColumn
          binding="returnedForCorrectionCount"
          header="# ΕΠΙΣΤΡ."
          width={105}
          align="right"
          format="d"
        />
      </FlexGrid>
    );
  };

  return (
    <Box
      sx={{
        m: 2.5,
        p: 2.5,
        borderRadius: 1,
        backgroundColor: "#f3f3f3",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "flex-start" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
          Σύνοψη Δαπανών ΜΗΔ
        </Typography>
        <ExpenseGuideLinks sx={{ width: { xs: "100%", md: "auto" } }} />
      </Stack>
      <CssTextField
        label="Αναζήτηση"
        placeholder="Αναζήτηση με ΜΗΔ, κατάσταση, φορέα ή ποσό"
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
        size="small"
        variant="outlined"
        InputProps={{
          disableUnderline: true,
        }}
        sx={{ mb: 2, width: { xs: "100%", md: 420 } }}
      />

      {renderExpensesContent()}
    </Box>
  );
};

export default MhdExpenseSummaryExplorer;
